import type { AgentSessionResponse } from "../api/generated/types/agentSessionResponse";

/**
 * A2A agent audio client.
 *
 * Connects the browser to the `relance-ai-agent` WebSocket and relays raw PCM16
 * (16kHz mono) both directions. The agent side runs the Gemini Live session
 * (server-owned WebSocket) and returns audio frames over the same socket.
 *
 * Upstream PCM chunks are sent as binary WebSocket frames. Downstream audio
 * chunks (24kHz PCM16 from Gemini) arrive as `ArrayBuffer` messages.
 */

export interface AgentLiveClientOptions {
  session: AgentSessionResponse;
  onAudioFrame(chunk: ArrayBuffer): void;
  onDebugStateChange?: (state: AgentLiveClientDebugState) => void;
  onFatalError?: (message: string) => void;
  onTranscript?: (speaker: "CANDIDATE" | "INTERVIEWER", text: string, final: boolean) => void;
  onInterviewEnded?: () => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
}

export interface AgentLiveClientDebugState {
  wsState: "idle" | "connecting" | "open" | "closed" | "error";
  audioFramesReceived: number;
  audioBytesReceived: number;
  lastAudioFrameAt: number | null;
  lastAudioFrameByteLength: number | null;
  lastMessageKind: "none" | "audio" | "fatal_error" | "text";
  lastError: string | null;
}

type AgentLiveControlMessage =
  | { type: "activity_start" | "activity_end" }
  | { type: "candidate_done"; reason: "candidate_said_done" | "manual_button" };

interface AgentServerFatalMessage {
  type: "fatal_error";
  message: string;
}

function parseAgentServerFatalMessage(raw: string): AgentServerFatalMessage | null {
  try {
    const parsed = JSON.parse(raw) as { type?: string; message?: string };
    if (parsed.type === "fatal_error" && typeof parsed.message === "string") {
      return { type: parsed.type, message: parsed.message };
    }
  } catch {
    return null;
  }

  return null;
}

export class AgentLiveClient {
  private ws: WebSocket | null = null;
  private readonly opts: AgentLiveClientOptions;
  private debugState: AgentLiveClientDebugState = {
    wsState: "idle",
    audioFramesReceived: 0,
    audioBytesReceived: 0,
    lastAudioFrameAt: null,
    lastAudioFrameByteLength: null,
    lastMessageKind: "none",
    lastError: null,
  };
  private lastDebugEmitAt = 0;

  constructor(opts: AgentLiveClientOptions) {
    this.opts = opts;
    this.emitDebugState();
  }

  async connect(): Promise<void> {
    const { session } = this.opts;
    const url = new URL(session.agentAudioUrl);
    url.searchParams.set("token", session.clientAudioToken);
    this.setDebugState({ wsState: "connecting", lastError: null });

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url.toString());
      ws.binaryType = "arraybuffer";
      ws.onopen = () => {
        this.setDebugState({ wsState: "open" });
        this.opts.onOpen?.();
        resolve();
      };
      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          this.setDebugState(
            {
              audioFramesReceived: this.debugState.audioFramesReceived + 1,
              audioBytesReceived: this.debugState.audioBytesReceived + event.data.byteLength,
              lastAudioFrameAt: Date.now(),
              lastAudioFrameByteLength: event.data.byteLength,
              lastMessageKind: "audio",
            },
            false
          );
          this.opts.onAudioFrame(event.data);
          return;
        }

        if (typeof event.data === "string") {
          let parsed: { type?: string; speaker?: string; text?: string; final?: boolean } | null =
            null;
          try {
            parsed = JSON.parse(event.data) as {
              type?: string;
              speaker?: string;
              text?: string;
              final?: boolean;
            };
          } catch {
            parsed = null;
          }
          if (
            parsed?.type === "transcript" &&
            (parsed.speaker === "CANDIDATE" || parsed.speaker === "INTERVIEWER")
          ) {
            this.setDebugState({ lastMessageKind: "text" }, false);
            this.opts.onTranscript?.(
              parsed.speaker,
              typeof parsed.text === "string" ? parsed.text : "",
              parsed.final === true
            );
            return;
          }
          if (parsed?.type === "interview_ended") {
            this.setDebugState({ lastMessageKind: "text" });
            this.opts.onInterviewEnded?.();
            return;
          }
          const fatal = parseAgentServerFatalMessage(event.data);
          if (!fatal) {
            this.setDebugState({ lastMessageKind: "text" });
            return;
          }
          this.setDebugState({ lastError: fatal.message, lastMessageKind: "fatal_error" });
          this.opts.onFatalError?.(fatal.message);
        }
      };
      ws.onerror = (event) => {
        this.setDebugState({ wsState: "error", lastError: "agent websocket error" });
        this.opts.onError?.(event);
        reject(new Error("agent websocket error"));
      };
      ws.onclose = (event) => {
        this.setDebugState({
          wsState: "closed",
          ...(event.reason ? { lastError: event.reason } : {}),
        });
        this.opts.onClose?.(event);
      };
      this.ws = ws;
    });
  }

  sendAudio(chunk: ArrayBuffer | Uint8Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    this.ws.send(chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk));
  }

  sendActivityStart(): void {
    this.sendControl({ type: "activity_start" });
  }

  sendActivityEnd(): void {
    this.sendControl({ type: "activity_end" });
  }

  sendCandidateDone(reason: "candidate_said_done" | "manual_button"): void {
    this.sendControl({ type: "candidate_done", reason });
  }

  close(): void {
    if (!this.ws) return;
    try {
      this.ws.close();
    } catch {
      // no-op
    }
    this.ws = null;
    this.setDebugState({ wsState: "closed" });
  }

  private setDebugState(next: Partial<AgentLiveClientDebugState>, force = true) {
    this.debugState = {
      ...this.debugState,
      ...next,
    };
    this.emitDebugState(force);
  }

  private emitDebugState(force = true) {
    if (!this.opts.onDebugStateChange) {
      return;
    }
    const now = Date.now();
    if (!force && now - this.lastDebugEmitAt < 250) {
      return;
    }
    this.lastDebugEmitAt = now;
    this.opts.onDebugStateChange?.(this.debugState);
  }

  private sendControl(message: AgentLiveControlMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    this.ws.send(JSON.stringify(message));
  }
}
